import React from 'react';
import { Radio, ListFilter, BarChart, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface StepProps {
  number: number;
  title: string;
  text: string;
  icon: React.ReactNode;
  delay: string;
}

const Step: React.FC<StepProps> = ({ number, title, text, icon, delay }) => {
  return (
    <div 
      className="flex items-start animate-on-scroll" 
      style={{ transitionDelay: delay }}
    >
      <div className="mr-6 relative">
        <div className="w-12 h-12 rounded-full bg-accent-gradient flex items-center justify-center animate-glowPulse">
          <span className="font-heading font-bold">{number}</span>
        </div>
        {number < 4 && (
          <div className="absolute top-12 left-1/2 transform -translate-x-1/2 w-px h-20 bg-gradient-to opacity-30 hidden md:block"></div>
        )}
      </div>
      <div>
        <div className="text-gradient-from mb-3">{icon}</div>
        <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
        <p className="text-text-secondary max-w-md">{text}</p>
      </div>
    </div>
  );
};

const HowItWorks: React.FC = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: <Radio size={24} />,
      title: t('howItWorks.step1.title'),
      text: t('howItWorks.step1.text')
    },
    {
      icon: <ListFilter size={24} />,
      title: t('howItWorks.step2.title'),
      text: t('howItWorks.step2.text')
    },
    {
      icon: <BarChart size={24} />,
      title: t('howItWorks.step3.title'),
      text: t('howItWorks.step3.text')
    },
    {
      icon: <Target size={24} />,
      title: t('howItWorks.step4.title'),
      text: t('howItWorks.step4.text')
    }
  ];

  return (
    <section id="engine" className="section-spacing bg-bg-secondary relative">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center animate-on-scroll">
          {t('howItWorks.heading')}
        </h2>
        
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-12 md:gap-6">
          {steps.map((step, index) => (
            <Step
              key={index}
              number={index + 1}
              title={step.title}
              text={step.text}
              icon={step.icon}
              delay={`${0.1 * (index + 1)}s`}
            />
          ))}
        </div>

        <p className="text-center text-text-secondary mt-16 text-lg animate-on-scroll">
          {t('howItWorks.precision')}
        </p>
      </div>
    </section>
  );
};

export default HowItWorks;